import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { PostIRLDocuments } from '../../../redux/documentsSlice';
import { createDocxBlobFromHtml } from '../../Journal/exportNote';
import { sanitizeRichTextForFileExport } from '../../../utils/sanitizeRichTextForFileExport';
import styles from '../css/vdr.module.css';

const EMPTY_QUILL_CONTENT = '<p><br></p>';

const FILE_FORMATS = [
    { value: 'txt', label: 'Text (.txt)', extension: 'txt', mimeType: 'text/plain' },
    {
        value: 'docx',
        label: 'Word (.docx)',
        extension: 'docx',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
];

const isEmptyContent = html => !html || html === EMPTY_QUILL_CONTENT || html.replace(/<[^>]*>/g, '').trim() === '';

const sanitizeFileName = name =>
    name
        .trim()
        .replace(/[/\\?%*:|"<>]/g, '')
        .replace(/\.(txt|docx)$/i, '');

const htmlToPlainText = html => {
    if (typeof document === 'undefined') {
        return html.replace(/<[^>]*>/g, '');
    }
    const el = document.createElement('div');
    el.innerHTML = html;
    return el.innerText || el.textContent || '';
};

const buildFileFromContent = async (format, content) => {
    const formatConfig = FILE_FORMATS.find(item => item.value === format) || FILE_FORMATS[0];

    if (format === 'docx') {
        const blob = await createDocxBlobFromHtml({ bodyHtml: content, includeLogo: false, includeTitle: false });
        return { blob, extension: formatConfig.extension, mimeType: formatConfig.mimeType };
    }

    if (format === 'txt') {
        const blob = new Blob([htmlToPlainText(content)], { type: formatConfig.mimeType });
        return { blob, extension: formatConfig.extension, mimeType: formatConfig.mimeType };
    }

    throw new Error(`Unsupported file format: ${format}`);
};

/**
 * Create a text or Word file in the current folder — same flow as classic dataroom.
 */
const CreateFileModal = ({ open, onClose, workstream, folder }) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { projectUsersAll } = useSelector(state => state?.team);
    const { user } = useSelector(state => state?.user);
    const [fileName, setFileName] = React.useState('');
    const [fileFormat, setFileFormat] = React.useState('txt');
    const [content, setContent] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [prevOpen, setPrevOpen] = React.useState(open);

    if (open !== prevOpen) {
        setPrevOpen(open);
        if (open) {
            setFileName('');
            setFileFormat('txt');
            setContent('');
            setLoading(false);
        }
    }

    if (!open) return null;

    const destination = [workstream?.name, folder?.name].filter(Boolean).join(' → ') || 'VDR';

    const handleSubmit = async () => {
        const trimmedName = sanitizeFileName(fileName);
        if (!workstream?.id || !folder?.id) {
            toast.error('Select a folder first.');
            return;
        }
        if (!trimmedName) {
            toast.error('Please enter a file name.');
            return;
        }
        if (isEmptyContent(content)) {
            toast.error('Please enter some content.');
            return;
        }

        const sanitizedContent = sanitizeRichTextForFileExport(content);
        if (isEmptyContent(sanitizedContent)) {
            toast.error('Content contains unsupported or unsafe markup and cannot be saved.');
            return;
        }

        setLoading(true);
        try {
            const { blob, extension, mimeType } = await buildFileFromContent(fileFormat, sanitizedContent);
            const fullFileName = `${trimmedName}.${extension}`;
            const file = new File([blob], fullFileName, { type: mimeType });

            const body = new FormData();
            body.append('document', file);
            body.append('document_name', fullFileName);
            body.append('ws_folder', folder.id);
            body.append('project', selectedProject?.id);
            body.append('vdr', selectedProject?.vdr?.id);
            if (selectedProject?.vdr?.vdr_admin !== user?.id) {
                body.append('upload_by', projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id);
            }

            const res = await dispatch(PostIRLDocuments({ body, ws_id: workstream.id }));
            if (res?.type === 'POST_IRL_DOC/rejected') {
                toast.error(res?.payload?.detail || 'Failed to save file.');
                return;
            }
            toast.success('File saved successfully.');
            onClose();
        } catch (error) {
            console.error('Error saving text/doc file:', error);
            toast.error('Failed to save file.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={`${styles.modal} ${styles.modalWide}`}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Create a file</h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <p className={styles.destinationLine}>
                        Destination: <span className={styles.destinationPath}>{destination}</span>
                    </p>
                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>
                            File name <span className={styles.requiredMark}>*</span>
                        </label>
                        <input
                            className={styles.input}
                            value={fileName}
                            onChange={e => setFileName(e.target.value)}
                            placeholder="Enter file name"
                        />
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>Save as</label>
                        <div className={styles.selectWrap}>
                            <select className={styles.input} value={fileFormat} onChange={e => setFileFormat(e.target.value)}>
                                {FILE_FORMATS.map(format => (
                                    <option key={format.value} value={format.value}>
                                        {format.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>Content</label>
                        <ReactQuill
                            useSemanticHTML={false}
                            placeholder="Enter content..."
                            modules={{
                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                            }}
                            theme="snow"
                            className={styles.fileEditor}
                            value={content}
                            onChange={setContent}
                        />
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button type="button" className={styles.primaryBtn} onClick={handleSubmit} disabled={loading}>
                        Save {loading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                    </button>
                    <button type="button" className={`${styles.secondaryBtn} ${styles.modalCancel}`} onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateFileModal;
